import React from "react";
import PropTypes from "prop-types";
import { DownOutlined, RightOutlined, LoadingOutlined } from "@ant-design/icons";

const HierarchyNode = ({
  node,
  expandedNodes,
  toggleNode,
  selectNode,
  selectedNode,
  loadingNodes = {},
  isRoot = false,
  level = 0,
}) => {
  const isExpanded = expandedNodes[node.id];
  const isLoading = loadingNodes[node.id];
  const hasChildren = node.hasDirectReports;
  const isSelected = selectedNode?.id === node.id;

  // Define colors for different levels
  const levelColors = [
    { border: '#3b82f6', bg: '#eff6ff', text: '#2563eb' }, // Level 0 - Blue
    { border: '#10b981', bg: '#ecfdf5', text: '#059669' }, // Level 1 - Green
    { border: '#f59e0b', bg: '#fffbeb', text: '#d97706' }, // Level 2 - Orange
    { border: '#8b5cf6', bg: '#f3e8ff', text: '#7c3aed' }, // Level 3 - Purple
    { border: '#ef4444', bg: '#fef2f2', text: '#dc2626' }, // Level 4 - Red
  ];

  const currentColor = levelColors[level % levelColors.length];

  // Handle card click - select the node
  const handleCardClick = () => {
    selectNode(node);
  };

  // Handle expand button click - toggle the node expansion
  const handleExpandClick = (e) => {
    e.stopPropagation(); // Prevent card click event
    toggleNode(node);
  };

  return (
    <div className={`tree-node ${isRoot ? 'root-node' : ''}`}>
      <div 
        className={`node-card ${isSelected ? "selected" : ""}`}
        style={{ 
          borderTopColor: isSelected ? currentColor.border : currentColor.border,
          backgroundColor: isSelected ? currentColor.bg : '#ffffff',
          cursor: 'pointer'
        }}
        onClick={handleCardClick}
      >
        {/* Node Info */}
        <div 
          className="node-content" 
          style={{ color: isSelected ? currentColor.text : '#1e293b' }}
        >
          <strong>{node.fullName}</strong>
          <div className="node-designation">{node.designationName}</div>
        </div>
        
        {/* Expand Button at Bottom Center */}
        {hasChildren && (
          <div className="expand-button-container">
            <button
              className="toggle-btn-bottom"
              disabled={isLoading}
              onClick={handleExpandClick}
            >
              {isLoading ? (
                <LoadingOutlined />
              ) : isExpanded ? (
                <DownOutlined />
              ) : (
                <RightOutlined />
              )}
            </button>
          </div>
        )}
      </div>

      {/* Children Container */}
      {isExpanded && (
        <div className="children-container">
          {/* Vertical line from parent to horizontal line */}
          <div className="vertical-line-parent"></div>
          
          {node.children && node.children.length > 0 && (
            <div className="horizontal-line-container">
              <div className="horizontal-line"></div>
            </div>
          )}
          
          <div className="children-nodes">
            {node.children && node.children.length > 0 ? (
              node.children.map((child) => (
                <div key={child.id} className="child-node-wrapper">
                  {/* Vertical line from horizontal line to child */}
                  <div className="vertical-line-child"></div>
                  <HierarchyNode
                    node={child}
                    expandedNodes={expandedNodes}
                    toggleNode={toggleNode}
                    selectNode={selectNode}
                    selectedNode={selectedNode}
                    loadingNodes={loadingNodes}
                    level={level + 1}
                  />
                </div>
              ))
            ) : (
              isLoading && (
                <div className="loading-children">
                  <LoadingOutlined /> Loading...
                </div>
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
};

HierarchyNode.propTypes = {
  node: PropTypes.object.isRequired,
  expandedNodes: PropTypes.object.isRequired,
  toggleNode: PropTypes.func.isRequired,
  selectNode: PropTypes.func.isRequired,
  selectedNode: PropTypes.object,
  loadingNodes: PropTypes.object,
  isRoot: PropTypes.bool,
  level: PropTypes.number,
};

export default HierarchyNode;